import { useMemo, useState } from 'react'
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'

import type { CategoryId, DailyResult, GameEvent } from '../game/types'
import { MAX_ATTEMPTS } from '../game/constants'
import {
  dayKey,
  dayIndex,
  getDailyPuzzle,
  getEndlessPuzzle,
  type DailyPuzzle,
} from '../game/daily'
import { gradeOrder, countCorrect, buildShareText, sharePlayUrl } from '../game/score'
import {
  readStats,
  recordResult,
  effectiveStreak,
  winPercent,
  type CategoryStats,
} from '../game/stats'
import { shareOrDownloadCard } from '../game/resultCard'
import { useDailyResult } from '../hooks/useDailyResult'
import { useLang } from '../i18n/LangContext'
import { EventCard } from './EventCard'
import { Countdown } from './Countdown'

function orderFromIds(ids: string[], events: GameEvent[]): GameEvent[] {
  const byId = new Map(events.map((e) => [e.id, e]))
  return ids.map((id) => byId.get(id)).filter((e): e is GameEvent => Boolean(e))
}

export function Game({
  category,
  onBack,
  mode = 'daily',
  nonce = 0,
  solvedCount = 0,
  onNext,
}: {
  category: CategoryId
  onBack: () => void
  mode?: 'daily' | 'endless'
  nonce?: number
  solvedCount?: number
  onNext?: (won: boolean) => void
}) {
  const daily = mode === 'daily'
  const today = dayKey()
  const todayIdx = dayIndex()
  const { t, localize } = useLang()

  const puzzle = useMemo(
    () => (daily ? getDailyPuzzle(category) : getEndlessPuzzle(category, nonce)),
    [daily, category, nonce],
  )
  const puzzleNo = daily ? (puzzle as DailyPuzzle).number : solvedCount + 1
  const refIds = useMemo(() => puzzle.events.map((e) => e.id), [puzzle])

  const { result, save } = useDailyResult(today, category, refIds)
  const restored = daily ? result : null
  const finishedFromStore = restored?.status === 'won' || restored?.status === 'lost'

  const [order, setOrder] = useState<GameEvent[]>(() =>
    restored ? orderFromIds(restored.order, puzzle.events) : puzzle.events,
  )
  const [checks, setChecks] = useState<boolean[][]>(() => restored?.checks ?? [])
  const [finished, setFinished] = useState<boolean>(() => Boolean(finishedFromStore))
  const [won, setWon] = useState<boolean>(() => restored?.status === 'won')
  const [shaking, setShaking] = useState(false)
  const [copied, setCopied] = useState(false)
  const [activeGrade, setActiveGrade] = useState<boolean[] | null>(null)
  const [stats, setStats] = useState<CategoryStats | null>(() =>
    daily && finishedFromStore ? readStats(category) : null,
  )

  const attemptsUsed = checks.length
  const attemptsLeft = MAX_ATTEMPTS - attemptsUsed

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    setOrder((items) => {
      const from = items.findIndex((i) => i.id === active.id)
      const to = items.findIndex((i) => i.id === over.id)
      if (from === -1 || to === -1) return items
      return arrayMove(items, from, to)
    })
    setActiveGrade(null)
  }

  function persist(next: Partial<DailyResult>) {
    save({
      day: today,
      category,
      order: order.map((e) => e.id),
      status: 'playing',
      attemptsUsed,
      checks,
      ...next,
    })
  }

  function onCheck() {
    if (finished) return
    const grade = gradeOrder(order, puzzle.solution)
    const correct = countCorrect(grade)
    const nextChecks = [...checks, grade]
    setChecks(nextChecks)
    setActiveGrade(grade)

    const solved = correct === order.length
    const outOfAttempts = nextChecks.length >= MAX_ATTEMPTS

    if (solved || outOfAttempts) {
      const didWin = solved
      setFinished(true)
      setWon(didWin)
      if (daily) {
        save({
          day: today,
          category,
          order: order.map((e) => e.id),
          status: didWin ? 'won' : 'lost',
          attemptsUsed: nextChecks.length,
          checks: nextChecks,
        })
        setStats(recordResult(category, todayIdx, didWin, nextChecks.length))
      }
    } else {
      setShaking(true)
      setTimeout(() => setShaking(false), 450)
      if (daily) persist({ checks: nextChecks, attemptsUsed: nextChecks.length })
    }
  }

  function shareText(): string {
    return buildShareText({
      categoryName: t.categories[category],
      puzzleNumber: puzzleNo,
      checks,
      won,
      maxAttempts: MAX_ATTEMPTS,
      url: sharePlayUrl(category),
    })
  }

  async function onShareText() {
    const text = shareText()
    const url = sharePlayUrl(category)
    try {
      if (navigator.share) {
        await navigator.share({ text, url })
      } else {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1800)
      }
    } catch {
      /* dismissed */
    }
  }

  function onShareImage() {
    void shareOrDownloadCard(
      {
        categoryName: t.categories[category],
        puzzleNumber: puzzleNo,
        checks,
        won,
        maxAttempts: MAX_ATTEMPTS,
        streak: stats ? effectiveStreak(stats, todayIdx) : 0,
      },
      shareText(),
    )
  }

  const display = (finished ? puzzle.solution : order).map(localize)
  const distMax = stats ? Math.max(1, ...stats.dist) : 1

  return (
    <section className="game">
      <div className="game-top">
        <button className="back" onClick={onBack}>
          ← {t.back}
        </button>
        <span className="game-tag">
          {t.categories[category]}{' '}
          <span className="game-no">
            {daily ? `№ ${puzzleNo}` : `· ${t.endless} ${solvedCount}`}
          </span>
        </span>
      </div>
      <div className="rule" />

      <p className="hint">{finished ? t.hintDone : t.hintPlay}</p>

      <div className="board">
        <span className="board-label">{t.earlier}</span>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={display.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className={`list ${finished ? 'revealed' : ''}`}>
              {display.map((ev, i) => (
                <EventCard
                  key={ev.id}
                  ev={ev}
                  index={i}
                  status={!finished && activeGrade?.[i] ? 'correct' : 'idle'}
                  revealed={finished}
                  shake={shaking && !finished && activeGrade?.[i] === false}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        <span className="board-label">{t.later}</span>
      </div>

      <div className="foot">
        {finished ? (
          <>
            <p className="result-line">
              {won
                ? t.resultWon(attemptsUsed, MAX_ATTEMPTS)
                : t.resultLost(Math.max(...checks.map(countCorrect), 0), order.length)}
            </p>

            {daily && stats && (
              <>
                <div className="stats">
                  <div className="stat">
                    <span className="stat-n">{effectiveStreak(stats, todayIdx)}</span>
                    <span className="stat-l">{t.statStreak}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-n">{stats.maxStreak}</span>
                    <span className="stat-l">{t.statBest}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-n">{stats.played}</span>
                    <span className="stat-l">{t.statPlayed}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-n">{winPercent(stats)}</span>
                    <span className="stat-l">{t.statWin}</span>
                  </div>
                </div>
                <div className="dist">
                  {stats.dist.map((c, i) => {
                    const hit = won && attemptsUsed === i + 1
                    return (
                      <div className="dist-row" key={i}>
                        <span className="dist-k">{i + 1}</span>
                        <span className="dist-track">
                          <span
                            className={`dist-bar ${hit ? 'hit' : ''}`}
                            style={{ width: `${(c / distMax) * 100}%` }}
                          />
                        </span>
                        <span className="dist-c">{c}</span>
                      </div>
                    )
                  })}
                </div>
              </>
            )}

            <div className="btn-row">
              {!daily && (
                <button className="btn" onClick={() => onNext?.(won)}>
                  {t.nextRound}
                  <span className="btn-arrow" aria-hidden="true">→</span>
                </button>
              )}
              {daily && (
                <button className="btn" onClick={onShareText}>
                  {copied ? t.copied : t.shareResult}
                </button>
              )}
              <button className="btn ghost" onClick={onShareImage}>
                {t.shareImage}
              </button>
            </div>

            {daily && <Countdown />}
          </>
        ) : (
          <>
            <div className="attempts" aria-label={t.checksLeft(attemptsLeft, MAX_ATTEMPTS)}>
              {Array.from({ length: MAX_ATTEMPTS }).map((_, i) => (
                <span key={i} className={`tick ${i < attemptsLeft ? 'on' : ''}`} />
              ))}
              <span className="attempts-label">
                {t.checksLeft(attemptsLeft, MAX_ATTEMPTS)}
              </span>
            </div>
            <button className="btn" onClick={onCheck}>
              {checks.length === 0 ? t.checkOrder : t.checkAgain}
              <span className="btn-arrow" aria-hidden="true">→</span>
            </button>
          </>
        )}
      </div>
    </section>
  )
}
